import { useState, useMemo, useLayoutEffect } from 'react';
import { Outlet, useNavigate, useLocation, useMatches } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useResponsive } from '@/shared/hooks/useResponsive.js';
import { removeCookie } from '@/shared/utils/cookies.js';

import Header from '@/layout/Header.jsx';
import Sidebar from '@/layout/Sidebar.jsx';
import SecondarySidebar from '@/layout/SecondarySidebar.jsx';
import MobileHeader from '@/layout/mobile/MobileHeader.jsx';
import MobileSidebar from '@/layout/mobile/MobileSidebar.jsx';
import MobileBottomNav from '@/layout/mobile/MobileBottomNav.jsx';
import UnderConstructionPage from '@/feature/Errors/pages/UnderConstructionPage.jsx';

import { billingSecondarySidebar } from '@/feature/Billing/configs/sidebar.config.js';
import { rulesSecondarySidebar } from '@/feature/Rules/configs/sidebar.config.js';

const featureConfigs = {
  '/rules': rulesSecondarySidebar,
  '/subscription': billingSecondarySidebar,
  '/credit': billingSecondarySidebar,
  '/invoices': billingSecondarySidebar,
  '/payments': billingSecondarySidebar,
  '/billing': billingSecondarySidebar,
};

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const matches = useMatches();

  const isCollapsed = useSelector((state) => state.sidebar.isCollapsed);

  const { isDesktop } = useResponsive();

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isPrimaryHovered, setIsPrimaryHovered] = useState(false);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const isUnderConstruction = useMemo(() => {
    return matches.some((match) => match.handle?.isUnderConstruction);
  }, [matches]);

  const secondarySidebarConfig = useMemo(() => {
    const matchedPath = Object.keys(featureConfigs).find((path) =>
      location.pathname.startsWith(path),
    );
    return matchedPath ? featureConfigs[matchedPath] : null;
  }, [location.pathname]);

  const hasSecondarySidebar = secondarySidebarConfig?.enabled || false;

  const handleMenuClick = () => setIsMobileSidebarOpen(true);
  const handleCloseSidebar = () => setIsMobileSidebarOpen(false);

  const handleLogout = () => {
    removeCookie('access_token');
    removeCookie('refresh_token');
    navigate('/login', { replace: true });
  };

  const getMainMargin = () => {
    if (!hasSecondarySidebar) return isCollapsed ? 'mr-16' : 'mr-72';
    if (isPrimaryHovered) return 'mr-[304px]';
    return 'mr-[280px]';
  };

  if (isUnderConstruction) {
    return <UnderConstructionPage />;
  }

  if (isDesktop) {
    return (
      <div className="text-tertiary-100 min-h-screen bg-slate-50 transition-colors duration-300 dark:bg-gray-900 dark:text-gray-50">
        <Sidebar
          hasSecondarySidebar={hasSecondarySidebar}
          onHoverChange={setIsPrimaryHovered}
        />
        {hasSecondarySidebar && (
          <SecondarySidebar
            config={secondarySidebarConfig}
            isMinimized={isPrimaryHovered}
          />
        )}
        <Header
          hasSecondarySidebar={hasSecondarySidebar}
          isPrimaryHovered={isPrimaryHovered}
        />
        <main
          className={`min-h-screen pt-16 transition-all duration-300 ease-in-out ${getMainMargin()}`}
        >
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="text-tertiary-100 min-h-screen bg-slate-50 transition-colors duration-300 dark:bg-gray-900 dark:text-gray-50">
      <MobileHeader onMenuClick={handleMenuClick} />
      <MobileSidebar
        isOpen={isMobileSidebarOpen}
        onClose={handleCloseSidebar}
      />
      <main
        className="min-h-screen"
        style={{
          paddingTop: 'calc(4rem + env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(4rem + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div className="p-4">
          <Outlet />
        </div>
      </main>
      <MobileBottomNav onLogout={handleLogout} />
    </div>
  );
}
